import { chains, MULTICALL3_ADDRESS, FALLBACK_RPC_PROVIDERS } from "./constants";
import { Contract, ethers } from "ethers";
import ERC20ABI from "./erc20-classic.json";

/** Returns array of RPC URLs to try for a chain (primary first, then fallbacks). */
function getRpcUrlsForChain(chainId) {
  const chain = chains[Number(chainId)];
  const primary = chain?.rpcUrl;
  const fallbacks = FALLBACK_RPC_PROVIDERS[Number(chainId)] || [];
  const all = [primary, ...fallbacks].filter(Boolean);
  return [...new Set(all)];
}

// Native token sentinel for batch balance calls (matches TokenSelector / existing helpers)
const NATIVE_SENTINEL = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';

const MULTICALL3_ABI = [
  'function tryAggregate(bool requireSuccess, tuple(address target, bytes callData)[] calls) payable returns (tuple(bool success, bytes returnData)[])',
  'function getEthBalance(address addr) view returns (uint256)'
];

// Memcache for token balances with 15-second expiry
const balanceCache = new Map();
const CACHE_DURATION = 15 * 1000; // 15 seconds in milliseconds

function getCacheKey(tokenAddress, userAddress, chainId) {
  return `${chainId}-${tokenAddress}-${userAddress}`;
}

function getCachedBalance(tokenAddress, userAddress, chainId) {
  const key = getCacheKey(tokenAddress, userAddress, chainId);
  const cached = balanceCache.get(key);

  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.balance;
  }

  // Remove expired cache entry
  if (cached) {
    balanceCache.delete(key);
  }

  return null;
}

function setCachedBalance(tokenAddress, userAddress, chainId, balance) {
  const key = getCacheKey(tokenAddress, userAddress, chainId);
  balanceCache.set(key, {
    balance,
    timestamp: Date.now()
  });
}

export function numberWithCommas(x) {
  if (typeof x !== "number") return "0.0";
  const ans = parseFloat(x).toLocaleString("en-US", { maximumFractionDigits: 4, minimumFractionDigits: 1 });
  return ans || '0.0'
}

export function formatAddress(addy, dots = 3, startCount = 6, endCount = 4) {
  if (typeof addy === "string") {
    const len = addy.length;
    return (
      addy.substring(0, startCount) +
      ".".repeat(dots) +
      addy.substring(len - endCount, len)
    );
  }
  return "";
}

/**
 * Format balance for display: preserve small nonzero values instead of collapsing to "0.0"
 */
export function formatBalanceDisplay(rawBalance) {
  if (rawBalance == null || rawBalance === '' || rawBalance === '...') return '0';
  const n = parseFloat(rawBalance);
  if (Number.isNaN(n)) return '0';
  if (n === 0) return '0';
  if (n >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (n >= 1) return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 4 });
  if (n < 0.0001) return n.toExponential(2);
  return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 6 });
}

export function countDecimalPlaces(number, min = 0, max = Infinity) {
  // Convert the number to a string
  const numString = number.toString();

  // Check if the number contains a decimal point
  const decimalIndex = numString.indexOf('.');

  // If there is no decimal point, return 0
  if (decimalIndex === -1) {
    return min;
  }

  // Calculate the length of characters after the decimal point
  const decimalPlaces = numString.length - decimalIndex - 1;

  return Math.min(Math.max(decimalPlaces, min), max);
}


export async function switchNetwork(chain, provider = window.ethereum) {
  try {
    // Check if chain exists in our constants
    if (!chains[chain]) {
      console.error(`Chain ${chain} not found in chains configuration`);
      return false;
    }

    // Check if provider is available and has request method
    if (!provider || typeof provider.request !== 'function') {
      console.error('No valid provider available for network switching');
      return false;
    }

    const hexChainId = chains[chain].hexChainId;
    if (process.env.NODE_ENV === "development") console.log(`Switching to chain ${chain} (${hexChainId})`);

    // Quick check if already on target chain
    try {
      const currentChain = await provider.request({ method: 'eth_chainId' });
      if (currentChain === hexChainId) {
        if (process.env.NODE_ENV === "development") console.log('Already on target chain');
        return true;
      }
    } catch (detectError) {
      // Continue with switch attempt if detection fails
    }

    // Switch to target chain
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: hexChainId }],
    });

    if (process.env.NODE_ENV === "development") console.log(`Successfully switched to chain ${chain}`);
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === "development") console.error('Error switching network:', error);

    // If the chain is not added to the wallet, try to add it
    if (error.code === 4902 || error.code === -32603) {
      try {
        if (process.env.NODE_ENV === "development") console.log(`Chain ${chain} not found in wallet, attempting to add it...`);
        await provider.request({
          method: 'wallet_addEthereumChain',
          params: [{
            chainId: chains[chain].hexChainId,
            chainName: chains[chain].name,
            nativeCurrency: {
              name: chains[chain].currency,
              symbol: chains[chain].currency,
              decimals: 18,
            },
            rpcUrls: [chains[chain].rpcUrl],
            blockExplorerUrls: [chains[chain].explorerUrl],
          }],
        });
        console.log(`Successfully added and switched to chain ${chain}`);
        return true;
      } catch (addError) {
        console.error('Error adding chain:', addError);
        return false;
      }
    }

    return false;
  }
};

export function toDeadline(expiration) {
  return Math.floor((Date.now() + expiration) / 1000)
}

export function isDai(currentToken, network) {
  return currentToken.address?.toLowerCase() === "0x6b175474e89094c44da98b954eedeac495271d0f" || (currentToken.name === "Dai Stablecoin" && (network === 1 || network === 42161)) || (currentToken.name === "(PoS) Dai Stablecoin" && network === 137)
}

export async function getTokenBalance(tokenAddress, userAddress, chainId) {
  if (!userAddress || !chainId) return 0;

  const cachedBalance = getCachedBalance(tokenAddress, userAddress, chainId);
  if (cachedBalance !== null) return cachedBalance;

  const rpcUrls = getRpcUrlsForChain(chainId);
  if (!rpcUrls.length) return 0;

  const chain = chains[Number(chainId)];
  const network = chain ? { chainId: Number(chainId), name: chain.name ?? 'unknown' } : undefined;
  let lastError;
  for (const rpcUrl of rpcUrls) {
    try {
      const provider = new ethers.JsonRpcProvider(rpcUrl, network);
      let balance;
      const isNative =
        !tokenAddress ||
        tokenAddress.length <= 6 ||
        tokenAddress.toLowerCase() === NATIVE_SENTINEL.toLowerCase();
      if (!isNative) {
        const tokenContract = new Contract(tokenAddress, ERC20ABI, provider);
        const rawBalance = await tokenContract.balanceOf(userAddress);
        const decimals = await tokenContract.decimals().catch(() => 18);
        balance = +(ethers.formatUnits(rawBalance, Number(decimals) || 18));
      } else {
        const rawBalance = await provider.getBalance(userAddress);
        balance = +(ethers.formatEther(rawBalance));
      }
      setCachedBalance(tokenAddress, userAddress, chainId, balance);
      return balance;
    } catch (error) {
      lastError = error;
      if (process.env.NODE_ENV === "development") {
        console.warn("RPC failed", rpcUrl, error?.message);
      }
    }
  }
  if (process.env.NODE_ENV === "development" && lastError) {
    console.warn("Error fetching token balance:", lastError.message);
  }
  return 0;
}

/**
 * Fetches balances for multiple tokens in one Multicall3 RPC call.
 * Used by the token list UI (e.g. TokenSelector) to show balances in batch.
 * @param {string[]} tokenAddresses - Array of addresses. Use NATIVE_SENTINEL (0xeee...eee) for native token.
 * @param {string} userAddress - User wallet address
 * @param {number} chainId - Chain ID
 * @returns {Promise<Record<string, string>>} Map of address (lowercase) -> formatted balance string, or "0" on failure
 */
export async function getTokenBalances(tokenAddresses, userAddress, chainId) {
  const result = {};
  if (!userAddress || !chainId || !tokenAddresses?.length) return result;
  const rpcUrls = getRpcUrlsForChain(chainId);
  if (!rpcUrls.length) return result;

  const erc20Interface = new ethers.Interface(ERC20ABI);
  const multicallInterface = new ethers.Interface(MULTICALL3_ABI);

  const fillZeros = () => tokenAddresses.forEach((addr) => { result[addr.toLowerCase()] = '0'; });

  const chain = chains[Number(chainId)];
  const network = chain ? { chainId: Number(chainId), name: chain.name ?? 'unknown' } : undefined;

  const decodeMulticallResults = (addresses, rawResults, meta) => {
    const decimalsByAddress = {};
    const balancesByAddress = {};

    rawResults.forEach((item, i) => {
      const callInfo = meta[i];
      if (!callInfo) return;
      const success = Array.isArray(item) ? item[0] : (item?.success ?? false);
      const oneReturnData = Array.isArray(item) ? item[1] : (item?.returnData ?? '0x');
      if (!success || !oneReturnData || oneReturnData === '0x') return;

      try {
        if (callInfo.type === 'decimals') {
          const decoded = ethers.AbiCoder.defaultAbiCoder().decode(['uint8'], oneReturnData);
          decimalsByAddress[callInfo.addr] = Number(decoded[0]);
        } else {
          const decoded = ethers.AbiCoder.defaultAbiCoder().decode(['uint256'], oneReturnData);
          balancesByAddress[callInfo.addr] = decoded[0];
        }
      } catch {
        // Ignore per-call decode failures and fall back to defaults below.
      }
    });

    const decodedResult = {};
    addresses.forEach((addr) => {
      const key = addr.toLowerCase();
      const raw = balancesByAddress[key];
      if (raw == null) {
        decodedResult[key] = '0';
        return;
      }
      const decimals = key === NATIVE_SENTINEL.toLowerCase()
        ? 18
        : (Number(decimalsByAddress[key]) || 18);
      decodedResult[key] = ethers.formatUnits(raw, decimals);
    });
    return decodedResult;
  };

  const fetchChunkWithMulticall = async (provider, addresses) => {
    const chunkCalls = [];
    const chunkMeta = [];
    const directNativeBalances = {};

    addresses.forEach((addr) => {
      const key = addr.toLowerCase();
      const isNative = key === NATIVE_SENTINEL.toLowerCase();

      if (isNative) {
        // Native balance is more reliable via provider.getBalance on some RPCs/chains.
        directNativeBalances[key] = null;
        return;
      }

      chunkCalls.push({
        target: addr,
        callData: erc20Interface.encodeFunctionData('balanceOf', [userAddress]),
      });
      chunkMeta.push({ addr: key, type: 'balance' });

      chunkCalls.push({
        target: addr,
        callData: erc20Interface.encodeFunctionData('decimals', []),
      });
      chunkMeta.push({ addr: key, type: 'decimals' });
    });

    // Fill native balances directly first.
    await Promise.allSettled(Object.keys(directNativeBalances).map(async (key) => {
      const nativeRaw = await provider.getBalance(userAddress);
      directNativeBalances[key] = ethers.formatEther(nativeRaw);
    }));

    // If chunk only contains native token(s), no multicall required.
    if (!chunkCalls.length) {
      const onlyNativeResult = {};
      addresses.forEach((addr) => {
        const key = addr.toLowerCase();
        onlyNativeResult[key] = directNativeBalances[key] ?? '0';
      });
      return onlyNativeResult;
    }

    const calldata = multicallInterface.encodeFunctionData('tryAggregate', [false, chunkCalls]);
    const returnData = await provider.call({ to: MULTICALL3_ADDRESS, data: calldata });
    const rawResults = multicallInterface.decodeFunctionResult('tryAggregate', returnData)[0];
    const decoded = decodeMulticallResults(addresses, rawResults, chunkMeta);

    // Override native values with direct provider balances when present.
    Object.entries(directNativeBalances).forEach(([key, value]) => {
      decoded[key] = value ?? decoded[key] ?? '0';
    });

    return decoded;
  };

  const fetchDirectBalances = async (provider) => {
    const directResult = {};
    await Promise.allSettled(tokenAddresses.map(async (addr) => {
      const key = addr.toLowerCase();
      try {
        if (key === NATIVE_SENTINEL.toLowerCase()) {
          const nativeRaw = await provider.getBalance(userAddress);
          directResult[key] = ethers.formatEther(nativeRaw);
          return;
        }
        const tokenContract = new Contract(addr, ERC20ABI, provider);
        const [rawBalance, decimals] = await Promise.all([
          tokenContract.balanceOf(userAddress),
          tokenContract.decimals().catch(() => 18),
        ]);
        directResult[key] = ethers.formatUnits(rawBalance, Number(decimals) || 18);
      } catch {
        directResult[key] = '0';
      }
    }));
    return directResult;
  };

  for (const rpcUrl of rpcUrls) {
    try {
      const provider = new ethers.JsonRpcProvider(rpcUrl, network);
      // Use chunked multicalls to avoid RPC call-size/gas limits on some chains.
      const CHUNK_SIZE = 16;
      for (let i = 0; i < tokenAddresses.length; i += CHUNK_SIZE) {
        const chunk = tokenAddresses.slice(i, i + CHUNK_SIZE);
        const partial = await fetchChunkWithMulticall(provider, chunk);
        Object.assign(result, partial);
      }
      tokenAddresses.forEach((addr) => {
        const key = addr.toLowerCase();
        if (result[key] == null) result[key] = '0';
      });
      return result;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('getTokenBalances RPC failed', rpcUrl, error?.message);
      }
      // If multicall path fails (e.g. chain-specific limits), retry with direct per-token calls.
      try {
        const provider = new ethers.JsonRpcProvider(rpcUrl, network);
        const directResult = await fetchDirectBalances(provider);
        tokenAddresses.forEach((addr) => {
          const key = addr.toLowerCase();
          result[key] = directResult[key] ?? '0';
        });
        return result;
      } catch (directError) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('getTokenBalances direct fallback failed', rpcUrl, directError?.message);
        }
      }
    }
  }
  fillZeros();
  return result;
}